import { splitLocale } from '@sas/nova-core/i18n/localeUtils';
import { ArabicPseudoTokens, ArabicTokens, ChineseChinaTokens, ChineseHongKongTokens, ChinesePseudoTokens, DarkTokens, HebrewTokens, HighContrastTokens, JapaneseTokens, KoreanTokens, LightTokens, TaiwaneseTokens, ThaiTokens, VietnameseTokens } from '@sas/product-tokens/js';
import { animation } from '../animation';
import { backgroundDepths, backgroundDepthsNova, backgrounds, backgroundsNova } from '../backgrounds';
import colors from '../colors';
import { spacing } from '../spacing';
import { stateFocus, stateFocusNova } from '../states/focus';
import { stateInputNova } from '../states/input-states';
import { statePrimary, statePrimaryContent, statePrimaryContentNova, statePrimaryNova } from '../states/primary-states';
import { stateSecondary, stateSecondaryNova, stateSecondaryToggle, stateSecondaryToggleNova } from '../states/secondary-states';
import { stateStandard, stateStandardContent, stateStandardContentNova, stateStandardNova } from '../states/standard-states';
import { stateStatus, stateStatusContent, stateStatusContentNova, stateStatusNova, stateStatusPadBox, stateStatusPadBoxNova } from '../states/status';
import { textTokens, textTokensNova } from '../text';
import { createFontStack, fontFamilyTokens, fontFamilyTokensNova, fontLetterSpacingTokens, fontLetterSpacingTokensNova, fontOverflowWrapTokens, fontOverflowWrapTokensNova, fontSizeTokens, fontSizeTokensNova, fontStyleTokens, fontStyleTokensNova, fontWeightTokens, fontWeightTokensNova, fontWordBreakTokens, fontWordBreakTokensNova, fontWordSpacingTokens, fontWordSpacingTokensNova, lineHeightTokens, lineHeightTokensNova, textTransformTokens, textTransformTokensNova } from '../typography';
export function renderReactDesignTokens(theme, parameters) {
  let productTheme = {
    ...LightTokens
  };
  switch (theme.baseThemeName) {
    case 'high-contrast':
      productTheme = {
        ...HighContrastTokens
      };
      break;
    case 'dark':
      productTheme = {
        ...DarkTokens
      };
      break;
  }
  const {
    lang,
    script,
    region
  } = splitLocale(parameters.lang.toLowerCase());
  let localeTokens;
  switch (lang) {
    case 'zh':
      if (parameters.lang.endsWith('pseudo')) {
        localeTokens = ChinesePseudoTokens;
      } else if (region === 'HK') {
        localeTokens = ChineseHongKongTokens;
      } else if (region === 'TW' || script === 'Hant') {
        localeTokens = TaiwaneseTokens;
      } else {
        localeTokens = ChineseChinaTokens;
      }
      break;
    case 'ja':
      localeTokens = JapaneseTokens;
      break;
    case 'ko':
      localeTokens = KoreanTokens;
      break;
    case 'th':
      localeTokens = ThaiTokens;
      break;
    case 'vi':
      localeTokens = VietnameseTokens;
      break;
    case 'he':
    case 'iw':
      localeTokens = HebrewTokens;
      break;
    case 'ar':
      if (region === 'XX') {
        localeTokens = ArabicPseudoTokens;
      } else {
        localeTokens = ArabicTokens;
      }
      break;
  }
  const tokensExperimental = {
    ...productTheme.tokens
  };
  if (localeTokens?.tokens) {
    Object.keys(localeTokens.tokens).forEach(name => {
      if (!name.startsWith('font-size')) {
        // @ts-ignore
        // ignoring due to readonly property
        tokensExperimental[name] = localeTokens.tokens[name];
      }
    });
  }
  if (theme.overrides?.fontFamily) {
    // @ts-ignore
    // ignoring due to readonly property
    tokensExperimental['font-family-primary'] = createFontStack(theme.overrides?.fontFamily, tokensExperimental['font-family-primary']);
    // @ts-ignore
    // ignoring due to readonly property
    tokensExperimental['font-family-secondary'] = createFontStack(theme.overrides?.fontFamily, tokensExperimental['font-family-secondary']);
  }
  const reportTheme = {
    ...theme,
    tokens: {
      ...tokensExperimental,
      ...theme.tokensExperimental
    }
  };
  const reportTokens = {
    // Old Tokens
    stateStandard: callStructWithArgs(stateStandard, reportTheme),
    stateStandardContent: callStructWithArgs(stateStandardContent, reportTheme),
    statePrimary: callStructWithArgs(statePrimary, reportTheme),
    statePrimaryContent: callStructWithArgs(statePrimaryContent, reportTheme),
    stateSecondary: callStructWithArgs(stateSecondary, reportTheme),
    stateSecondaryToggle: callStructWithArgs(stateSecondaryToggle, reportTheme),
    stateFocus: callStructWithArgs(stateFocus, reportTheme),
    stateStatus: callStructWithArgs(stateStatus, reportTheme),
    stateStatusPadBox: callStructWithArgs(stateStatusPadBox, reportTheme),
    stateStatusContent: callStructWithArgs(stateStatusContent, reportTheme),
    backgrounds: callStructWithArgs(backgrounds, reportTheme),
    backgroundsDepth: callStructWithArgs(backgroundDepths, reportTheme),
    font: {
      family: fontFamilyTokens(theme, parameters.lang),
      letterSpacing: fontLetterSpacingTokens(),
      overflowWrap: fontOverflowWrapTokens(),
      size: fontSizeTokens(theme),
      style: fontStyleTokens(),
      weight: fontWeightTokens(),
      wordBreak: fontWordBreakTokens(),
      wordSpacing: fontWordSpacingTokens(),
      lineHeight: lineHeightTokens(),
      textTransform: textTransformTokens()
    },
    text: {
      basic: textTokens.basic(theme),
      description: textTokens.description(theme),
      descriptionDisabled: textTokens.descriptionDisabled(theme),
      unread: textTokens.unread(theme),
      highlighted: textTokens.highlighted(theme),
      highlightedInFocus: textTokens.highlightedInFocus(theme),
      selection: textTokens.selection(theme)
    },
    c: callStructWithArgs(colors, theme)
  };
  const dt = {
    enableNovaTokens: theme.enableNovaTokens,
    themeVariant: theme.baseThemeName,
    baseReportThemeName: theme.baseReportThemeName,
    /**
     * @deprecated Use dt.tokens instead.
     */
    tokensExperimental,
    tokens: tokensExperimental,
    reportTokens,
    /**
     * Overrides that can be set by consumer themes. Used only in specific components.
     */
    overrides: {
      banner: {
        bg: theme.overrides?.bannerBg,
        fg: theme.overrides?.bannerFg,
        img: theme.overrides?.bannerImg
      },
      splash: {
        bg: theme.overrides?.splashBg,
        fg: theme.overrides?.splashFg,
        img: theme.overrides?.splashImg
      },
      transition: {
        bg: theme.overrides?.transitionBg,
        fg: theme.overrides?.transitionFg,
        img: theme.overrides?.transitionImg
      },
      table: {
        borderColor: theme.overrides?.tableBorderColor,
        cellBorderColor: theme.overrides?.tableCellBorderColor,
        headerBackground: theme.overrides?.tableHeaderBackground,
        headerBorderRightColor: theme.overrides?.tableHeaderBorderRightColor,
        headerLabelColor: theme.overrides?.tableHeaderLabelColor,
        rowBackgroundColorEven: theme.overrides?.tableRowBackgroundColorEven,
        rowBackgroundColorOdd: theme.overrides?.tableRowBackgroundColorOdd,
        textColor: theme.overrides?.tableTextColor,
        totalsBackgroundColor: theme.overrides?.tableTotalsBackgroundColor,
        totalsTextColor: theme.overrides?.tableTotalsTextColor
      },
      fontSizeBase: theme.overrides?.fontSizeBase
    },
    // Keep these on main dt object for backwards compatibility
    /**
     * @deprecated Use dt.reportTokens instead.
     */
    ...reportTokens,
    /**
     * @deprecated
     */
    stateStandardNova: callStructWithArgs(stateStandardNova, theme),
    /**
     * @deprecated
     */
    stateStandardContentNova: callStructWithArgs(stateStandardContentNova, theme),
    /**
     * @deprecated
     */
    statePrimaryNova: callStructWithArgs(statePrimaryNova, theme),
    /**
     * @deprecated
     */
    statePrimaryContentNova: callStructWithArgs(statePrimaryContentNova, theme),
    /**
     * @deprecated
     */
    stateSecondaryNova: callStructWithArgs(stateSecondaryNova, theme),
    /**
     * @deprecated
     */
    stateSecondaryToggleNova: callStructWithArgs(stateSecondaryToggleNova, theme),
    /**
     * @deprecated
     */
    stateStatusNova: callStructWithArgs(stateStatusNova, theme),
    /**
     * @deprecated
     */
    stateStatusPadBoxNova: callStructWithArgs(stateStatusPadBoxNova, theme),
    /**
     * @deprecated
     */
    stateStatusContentNova: callStructWithArgs(stateStatusContentNova, theme),
    /**
     * @deprecated
     */
    backgroundsDepthNova: callStructWithArgs(backgroundDepthsNova, theme),
    /**
     * @deprecated
     */
    backgroundsNova: callStructWithArgs(backgroundsNova, theme),
    /**
     * @deprecated
     */
    stateInputNova: callStructWithArgs(stateInputNova, theme),
    /**
     * @deprecated
     */
    stateFocusNova: callStructWithArgs(stateFocusNova, theme),
    /**
     * @deprecated
     */
    textNova: {
      selection: textTokensNova.selection(theme)
    },
    /**
     * @deprecated
     */
    spacing,
    /**
     * @deprecated
     */
    animation,
    /**
     * @deprecated
     */
    fontNova: {
      family: fontFamilyTokensNova(theme, parameters.lang),
      letterSpacing: fontLetterSpacingTokensNova(),
      overflowWrap: fontOverflowWrapTokensNova(),
      size: fontSizeTokensNova(theme, parameters.lang),
      style: fontStyleTokensNova(theme, parameters.lang),
      weight: fontWeightTokensNova(parameters.lang),
      wordBreak: fontWordBreakTokensNova(parameters.lang),
      wordSpacing: fontWordSpacingTokensNova(),
      lineHeight: lineHeightTokensNova(parameters.lang),
      textTransform: textTransformTokensNova(parameters.lang)
    }
  };
  return dt;
}
export function callStructWithArgs(struct, ...args) {
  const boundStruct = {};
  for (const key of Object.keys(struct)) {
    boundStruct[key] = struct[key](...args);
  }
  return boundStruct;
}
//# sourceMappingURL=react.js.map